import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Modal, Button, TextInput, Toggle} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {Icon} from '@pepsico-ds/ui';
import {
  useModalPointsValidation,
  useModalQuotaValidation,
  useModalInventoryValidation
} from '@/containers/Rewards/validations';
import {
  getInitialFormData,
  getInitialErrors,
  initializeFormDataFromProduct,
  createPointsHandler,
  createInventoryHandler,
  createQuotaHandler,
  createFormValidator,
  isInventoryDisabled
} from './productModalUtils';
import styles from './ProductDetailsModal.module.scss';

/**
 * ProductDetailsModal component for editing individual product details
 */
const ProductDetailsModal = ({
  showModal,
  onCloseModal,
  product,
  onApply,
  onCancel
}) => {
  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState(getInitialErrors());

  // Validation hooks using centralized validations
  const {onChange: validatePoints} = useModalPointsValidation();
  const {onChange: validateQuotaValue} = useModalQuotaValidation();
  const {onChange: validateInventory} = useModalInventoryValidation();

  // Initialize form data when product changes
  useEffect(() => {
    if (product?.details) {
      setFormData(initializeFormDataFromProduct(product));

      // Clear errors when product changes
      setErrors(getInitialErrors());
    }
  }, [product]);

  // Create handlers using shared utilities
  const handlePointsChange = createPointsHandler(
    validatePoints,
    setErrors,
    setFormData
  );
  const handleInventoryChange = createInventoryHandler(
    validateInventory,
    setErrors,
    setFormData
  );
  const handleQuotaChange = createQuotaHandler(
    validateQuotaValue,
    setErrors,
    setFormData,
    formData
  );

  // Create validator using shared utilities
  const isFormValid = createFormValidator(
    validatePoints,
    validateQuotaValue,
    formData
  );

  // Handle apply button click
  const handleApply = () => {
    if (isFormValid()) {
      onApply(formData);
      onCloseModal();
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    onCancel();
    onCloseModal();
  };

  // Handle modal close
  const handleCloseModal = () => {
    onCloseModal();
  };

  if (!product) return null;

  return (
    <Modal
      type={MODAL_TYPE.SMALL}
      hideCloseButton={true}
      showModal={showModal}
      title={product.productName}
      className={styles.productDetailsModalWrapper}
      isDark={true}
      showSecondaryButton={false}
      showPrimaryButton={false}
      onCloseModal={handleCloseModal}
      content={
        <div
          className={`flex flex-col gap-6 pb-4 pt-4 ${styles.productDetailsModal}`}>
          {/* Points Field */}
          <TextInput
            label='Points*'
            value={formData.points}
            onChange={handlePointsChange}
            placeholderText='Add Points'
            error={errors.points}
            isDark={true}
            type='number'
          />

          {/* Quota Section */}
          <div className='flex flex-col gap-2'>
            <p className='flex items-center gap-2 text-sm font-medium text-white'>
              Quota*{' '}
              <span className='text-xs text-white'>
                <Icon icon='info' size='small' />
              </span>
            </p>

            {/* Unlimited Toggle */}
            <Toggle
              value={formData.quota.unlimited}
              updateValue={(value) => handleQuotaChange('unlimited', value)}
              uiSchema={{
                label: 'Unlimited Quota',
                size: 'small',
                isDark: true,
                variant: 'horizontal',
                showCheckedIcon: false
              }}
              disabled={false}
            />

            {/* Quota Value Input - Only show when unlimited is false */}
            {!formData.quota.unlimited && (
              <TextInput
                label='Quota Value*'
                value={formData.quota.value}
                onChange={(value) => handleQuotaChange('value', value)}
                placeholderText='Add Quota Value'
                error={errors.quota}
                isDark={true}
                type='text'
              />
            )}
          </div>

          {/* Inventory Field */}
          <TextInput
            label='Inventory'
            value={formData.inventory}
            onChange={handleInventoryChange}
            error={errors.inventory}
            isDark={true}
            type='text'
            disabled={isInventoryDisabled()}
          />

          {/* Action Buttons */}
          <div className='flex justify-end gap-4'>
            <Button
              handleButton={handleCancel}
              uiSchema={{
                variant: 'tertiary',
                size: 'medium'
              }}>
              Cancel
            </Button>
            <Button
              handleButton={handleApply}
              uiSchema={{
                variant: 'primary',
                size: 'medium',
                disabled: !isFormValid()
              }}>
              Apply
            </Button>
          </div>
        </div>
      }
    />
  );
};

ProductDetailsModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  product: PropTypes.shape({
    productId: PropTypes.string,
    productName: PropTypes.string,
    details: PropTypes.shape({
      points: PropTypes.string,
      quota: PropTypes.shape({
        unlimited: PropTypes.bool,
        value: PropTypes.string
      }),
      inventory: PropTypes.string
    })
  }),
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ProductDetailsModal;
